import React, { useState } from 'react';
import { useRouter, useParams, usePathname } from 'next/navigation';
import Link from 'next/link';
import { Locales } from '@/types/i18n';
import { setLocaleCookie } from '@/lib/i18n/setLocalCookie';
import { getNewPathname } from '@/lib/i18n/getNewPathName';
import i18nConfig from '@i18nConfig';
import styles from './MobileDrawer.module.css';
import StampLogo from '../logo/StampLogo';
import CloseX from '../icons/CloseX';
import { withMarketQuery } from '@/lib/utils/withMarketQuery';

// Import translations
import enTranslation from '@/locales/en/translation.json';
import esTranslation from '@/locales/es/translation.json';
import deTranslation from '@/locales/de/translation.json';
import itTranslation from '@/locales/it/translation.json';
import zhCNTranslation from '@/locales/zh_CN/translation.json';

// Define a type for nested translation objects
type NestedTranslation = {
  [key: string]: string | NestedTranslation;
};

// Helper function to get translation based on locale
const getTranslation = (locale: Locales, key: string): string => {
  const keys = key.split('.');
  let translationObj: NestedTranslation;

  switch (locale) {
    case 'en':
      translationObj = enTranslation;
      break;
    case 'es':
      translationObj = esTranslation;
      break;
    case 'de':
      translationObj = deTranslation;
      break;
    case 'it':
      translationObj = itTranslation;
      break;
    case 'zh-CN':
      translationObj = zhCNTranslation;
      break;
    default:
      translationObj = enTranslation;
  }

  // Navigate through the nested keys
  let result: string | NestedTranslation = translationObj;
  for (const k of keys) {
    if (result && typeof result === 'object' && k in result) {
      result = result[k];
    } else {
      return key; // Return the key if translation not found
    }
  }

  return typeof result === 'string' ? result : key;
};

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const TravellersMobileDrawer: React.FC<MobileDrawerProps> = ({ open, onClose }) => {
  const [showHelpOptions, setShowHelpOptions] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const locale = (params.locale as Locales) || 'en';

  const isMerchantsPage = pathname.includes('/merchants');
  const faqsPath = isMerchantsPage ? `/${locale}/merchants/faqs` : `/${locale}/travelers/faqs`;

  const toggleHelpOptions = () => {
    setShowHelpOptions(!showHelpOptions);
  };
  if (!open) return null;
  return (
    <div className={styles.overlay}>
      <aside className={styles.drawer}>
        <div className={styles.headerRow}>
          <StampLogo className={styles.logo} />
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close menu">
            <CloseX />
          </button>
        </div>
        <div className={styles.menuContent}>
          <div className={styles.selectRow}>
            <div className={styles.helpDropdownMobile}>
              <button onClick={toggleHelpOptions} className={styles.helpButton}>
                {getTranslation(locale, 'navbar.help')}
              </button>
              {showHelpOptions && (
                <div className={styles.helpOptionsList}>
                  <Link
                    href={withMarketQuery(`/${locale}/travelers/guide`)}
                    className={styles.helpOption}
                    onClick={onClose}
                  >
                    {getTranslation(locale, 'navbar.guide')}
                  </Link>
                  <Link
                    href={withMarketQuery(faqsPath)}
                    className={styles.helpOption}
                    onClick={onClose}
                  >
                    {getTranslation(locale, 'navbar.faqs')}
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div className={styles.selectRow}>
            <select
              className={styles.select}
              value={locale}
              onChange={(e) => {
                const newLocale = e.target.value as Locales;
                setLocaleCookie(newLocale);
                let newPath = getNewPathname(newLocale, locale);

                // Preserve market parameter if it exists
                if (typeof window !== 'undefined') {
                  const params = new URLSearchParams(window.location.search);
                  const market = params.get('market');
                  if (market) {
                    newPath += (newPath.includes('?') ? '&' : '?') + `market=${market}`;
                  }
                }

                router.push(newPath);
                onClose(); // Close the drawer after changing language
              }}
            >
              {i18nConfig.languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </aside>
      <div className={styles.backdrop} onClick={onClose} />
    </div>
  );
};
